<template>
  <div class="w-full overflow-y-auto rounded-lg bg-eblack-100/70">
    <div
      class="flex flex-col gap-y-4 overflow-y-auto border-b border-gray-200 bg-slate-200 p-3"
    >
      <div class="flex flex-col gap-y-2">
        <label for="dni" class="text-sm md:text-md">{{ $t("settings.auto.identificacion") }}</label>
        <input
          id="dni"
          v-model.trim="userForm.dni"
          type="text"
          name="dni"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
          placeholder="000000000"
          :class="{ 'ring-red-400': errorFor('dni') }"
        />
        <p v-if="errorFor('dni')" class="pl-2 text-xs text-red-400 md:text-sm">
          {{ errorFor("dni") }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="assigned_person" class="text-sm md:text-md"
          >{{ $t("settings.auto.nombre_completo") }}</label
        >
        <input
          id="assigned_person"
          v-model.trim="userForm.assigned_person"
          type="text"
          name="assigned_person"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
          :placeholder="$t('settings.auto.nombre_y_apellidos')"
          :class="{ 'ring-red-400': errorFor('assigned_person') }"
        />
        <p
          v-if="errorFor('assigned_person')"
          class="pl-2 text-xs text-red-400 md:text-sm"
        >
          {{ errorFor("assigned_person") }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="email" class="text-sm md:text-md">{{ $t("settings.auto.correo") }}</label>
        <input
          id="email"
          v-model.trim="userForm.email"
          type="email"
          name="email"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
          placeholder="usuario@emanta.com"
          :class="{ 'ring-red-400': errorFor('email') }"
        />
        <p
          v-if="errorFor('email')"
          class="pl-2 text-xs text-red-400 md:text-sm"
        >
          {{ errorFor("email") }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="contact_phone" class="text-sm md:text-md">{{ $t("settings.auto.telefono") }}</label>
        <PhoneInput
          id="contact_phone"
          v-model="userForm.contact_phone"
          name="contact_phone"
          :invalid="Boolean(errorFor('contact_phone'))"
        />
        <p
          v-if="errorFor('contact_phone')"
          class="pl-2 text-xs text-red-400 md:text-sm"
        >
          {{ errorFor("contact_phone") }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="role_key" class="text-sm md:text-md">{{ $t("settings.auto.rol") }}</label>
        <select
          id="role_key"
          v-model="selectedRole"
          name="role_key"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
          :class="{ 'ring-red-400': errorFor('role_key') }"
        >
          <option value="">{{ $t("settings.auto.seleccione_un_rol") }}</option>
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.name }}
          </option>
        </select>
        <p
          v-if="errorFor('role_key')"
          class="pl-2 text-xs text-red-400 md:text-sm"
        >
          {{ errorFor("role_key") }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="preferred_language" class="text-sm md:text-md">{{ $t("settings.auto.idioma_de_la_app_y_correos") }}</label>
        <select
          id="preferred_language"
          v-model="userForm.preferred_language"
          name="preferred_language"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
        >
          <option
            v-for="language in utilityEmailLanguageOptions"
            :key="language.value"
            :value="language.value"
          >
            {{ language.label }}
          </option>
        </select>
      </div>

      <div v-if="isUpdating" class="flex flex-col gap-y-2">
        <label for="membership_status" class="text-sm md:text-md"
          >{{ $t("settings.auto.estado_de_membresia") }}</label
        >
        <select
          id="membership_status"
          v-model="membershipStatus"
          name="membership_status"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
        >
          <option value="active">{{ $t("settings.auto.activo") }}</option>
          <option value="inactive">{{ $t("settings.auto.inactivo") }}</option>
        </select>
      </div>

      <div v-else class="flex flex-col gap-y-2">
        <label for="password" class="text-sm md:text-md"
          >{{ $t("settings.auto.contrasena_temporal") }}</label
        >
        <input
          id="password"
          v-model="userForm.password"
          type="password"
          name="password"
          class="rounded-md text-sm md:text-md right-1 focus:ring-blue-400"
          :placeholder="$t('settings.auto.minimo_8_caracteres')"
          :class="{ 'ring-red-400': errorFor('password') }"
        />
        <p
          v-if="errorFor('password')"
          class="pl-2 text-xs text-red-400 md:text-sm"
        >
          {{ errorFor("password") }}
        </p>
      </div>

      <div class="mt-auto flex items-start">
        <button
          type="button"
          class="mb-2 rounded-md bg-green-500 px-2 py-1.5 text-white"
          @click="submit()"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/domains/auth";
import { useOrganizationStore } from "@/domains/organization";
import usersRequests from "@/services/admin/users-service";
import { utilityEmailLanguageOptions } from "@/shared/language/language-preferences";
import PhoneInput from "@/components/ui/PhoneInput.vue";
import { resolveApiErrorText } from "@/utils/api-error";

export default {
  props: ["pUser"],
  name: "FormTab",
  components: {
    PhoneInput,
  },
  data() {
    return {
      buttonText: "Guardar",
      errors: [],
      roles: [],
      selectedRole: "",
      membershipStatus: "active",
      userForm: {
        dni: "",
        assigned_person: "",
        email: "",
        contact_phone: "",
        password: "emanta123",
        ip_address: "127.0.0.1",
        preferred_language: "es",
      },
      utilityEmailLanguageOptions,
      isUpdating: false,
      userId: "",
      toast: useToast(),
    };
  },
  watch: {
    pUser: {
      handler(value) {
        if (value) {
          this.userId = value.id ?? value.user_id;
          this.userForm = {
            dni: value.dni ?? "",
            assigned_person: value.assigned_person ?? "",
            email: value.email ?? "",
            contact_phone: value.contact_phone ?? "",
            password: "",
            ip_address: value.ip_address ?? "127.0.0.1",
            preferred_language: value.preferred_language ?? "es",
          };
          this.selectedRole = value.role_key ?? value.roles?.[0]?.id ?? "";
          this.membershipStatus = value.membership_status ?? "active";
          this.buttonText = "Actualizar";
          this.isUpdating = true;
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },
  },
  setup() {
    const authStore = useAuthStore();
    const organizationStore = useOrganizationStore();
    return {
      authStore,
      organizationStore,
    };
  },
  methods: {
    getPermissions() {
      const orgType = this.authStore.currentOrgType;
      if (orgType === "provider") {
        this.roles = [
          { id: "provider_admin", name: "Administrador proveedor" },
          { id: "provider_staff", name: "Equipo proveedor" },
        ];
        return;
      }

      this.roles = [
        { id: "concierge_admin", name: "Administrador concierge" },
        { id: "concierge_staff", name: "Equipo concierge" },
      ];
    },
    errorFor(field) {
      if (!Array.isArray(this.errors)) return "";
      const match = this.errors.find((error) => error?.[field]);
      const detail = match?.[field];
      if (!detail) return "";
      if (typeof detail === "string") return detail;
      // Legacy API validation shape: { [field]: { constraints: string[], value, target } }
      if (Array.isArray(detail.constraints)) return detail.constraints.join(". ");
      return typeof detail.message === "string" ? detail.message : "";
    },
    submit() {
      this.errors = [];
      if (this.isUpdating) {
        this.updateUser();
      } else {
        this.saveUser();
      }
    },
    saveUser() {
      const currentOrgId = Number(this.authStore.currentOrgId);

      if (!currentOrgId || !this.selectedRole) {
        this.toast.error(i18n.global.t("settings.auto.seleccione_una_organizacion_y_un_rol"), {
          timeout: 3000,
          position: "top-right",
        });
        return;
      }

      this.organizationStore
        .addMember(currentOrgId, {
          dni: this.userForm.dni,
          assigned_person: this.userForm.assigned_person,
          email: this.userForm.email,
          contact_phone: this.userForm.contact_phone || null,
          password: this.userForm.password,
          ip_address: this.userForm.ip_address,
          preferred_language: this.userForm.preferred_language || "es",
          role_key: this.selectedRole,
        })
        .then(() => {
          this.toast.success(i18n.global.t("settings.auto.usuario_guardado"), {
            timeout: 3000,
            position: "top-right",
          });
          this.resetForm();
          this.$emit("close-modal");
        })
        .catch((error) => {
          this.handleRequestError(error, "settings.auto.hubo_un_problema_al_guardar");
        });
    },
    updateUser() {
      const currentOrgId = Number(this.authStore.currentOrgId);

      if (!currentOrgId || !this.userId || !this.selectedRole) {
        this.toast.error(i18n.global.t("settings.auto.seleccione_una_organizacion_y_un_rol"), {
          timeout: 3000,
          position: "top-right",
        });
        return;
      }

      Promise.all([
        usersRequests.update(this.userId, {
          dni: this.userForm.dni,
          assigned_person: this.userForm.assigned_person,
          email: this.userForm.email,
          contact_phone: this.userForm.contact_phone || null,
          preferred_language: this.userForm.preferred_language || "es",
        }),
        this.organizationStore.updateMember(currentOrgId, this.userId, {
          role_key: this.selectedRole,
          membership_status: this.membershipStatus,
        }),
      ])
        .then(() => {
          this.toast.success(i18n.global.t("settings.auto.usuario_actualizado"), {
            timeout: 3000,
            position: "top-right",
          });
          this.resetForm();
          this.$emit("close-modal");
        })
        .catch((error) => {
          this.handleRequestError(error, "settings.auto.hubo_un_problema_al_actualizar");
        });
    },
    handleRequestError(error, fallbackKey) {
      const { statusCode, message } = error?.response?.data ?? {};
      if (statusCode === 400 && Array.isArray(message)) {
        this.errors = message;
      }
      const text = resolveApiErrorText(
        error,
        (key, variables) => i18n.global.t(key, variables),
        i18n.global.t(fallbackKey),
      );
      this.toast.error(text, {
        timeout: 3000,
        position: "top-right",
      });
    },
    resetForm() {
      this.userForm = {
        dni: "",
        assigned_person: "",
        email: "",
        contact_phone: "",
        password: "emanta123",
        ip_address: "127.0.0.1",
        preferred_language: "es",
      };
      this.isUpdating = false;
      this.userId = "";
      this.buttonText = "Guardar";
      this.selectedRole = "";
      this.membershipStatus = "active";
      this.errors = [];
    },
  },
  mounted() {
    this.getPermissions();
  },
};
</script>
